import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, Treemap, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(188, 72%, 28%)', 'hsl(199, 89%, 48%)', 'hsl(36, 95%, 52%)', 'hsl(158, 64%, 39%)', 'hsl(2, 78%, 50%)'];

const Reportes = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.rol === 'SuperAdmin';
  const [stats, setStats] = useState(null);
  const [publicaciones, setPublicaciones] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [renta, setRenta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const requests = [
        axios.get(`${API}/stats`),
        axios.get(`${API}/publicaciones/paginated?limit=1000`),
        axios.get(`${API}/ofertas/paginated?limit=1000`)
      ];

      if (isSuperAdmin) {
        requests.push(axios.get(`${API}/stats/renta`).catch(() => ({ data: { renta: 0, totalAdjudicado: 0 } })));
      }

      const results = await Promise.all(requests);
      
      setStats(results[0].data);
      setPublicaciones(results[1].data?.items || []);
      setOfertas(results[2].data?.items || []);
      
      if (isSuperAdmin && results[3]) {
        setRenta(results[3].data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  const getOfertasPorDia = () => {
    if (!ofertas || ofertas.length === 0) return [];
    const counts = {};
    ofertas.forEach(o => {
      const d = new Date(o.hora || o.createdAt);
      if (isNaN(d.getTime())) return;
      const key = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([day, offers]) => ({ day, offers })).slice(-7);
  };
  const ofertasPorDia = getOfertasPorDia().length > 0 ? getOfertasPorDia() : [
    { day: '10/10', offers: 3 }, { day: '11/10', offers: 5 }
  ];

  // Estados de Ofertas
  const estadosData = stats ? [
    { name: 'Activas', value: stats.publicaciones_activas || 0, fill: '#3b82f6' },
    { name: 'Vencidas', value: stats.publicaciones_vencidas || 0, fill: '#ef4444' },
    { name: 'En Espera', value: stats.publicaciones_en_espera || 0, fill: '#eab308' },
    { name: 'Adjudicadas', value: stats.publicaciones_adjudicadas || stats.vehiculos_adjudicados || 0, fill: '#10b981' },
    { name: 'No Adjudicadas', value: stats.publicaciones_no_adjudicadas || 0, fill: '#64748b' }
  ].sort((a, b) => b.value - a.value) : [];

  // Distribución por Frigorífico
  const frigorificosCount = {};
  publicaciones.forEach((p) => {
    const frigo = p.frigorificoNombre || 'Desconocido';
    frigorificosCount[frigo] = (frigorificosCount[frigo] || 0) + 1;
  });
  const distribucionData = Object.entries(frigorificosCount).map(([name, value]) => ({ name, value }));

  // Comparación precio base vs adjudicado
  const preciosComparacion = publicaciones
    .filter((p) => p.estadoPublicacion === 'Adjudicada' && p.precioAdjudicado)
    .map((p) => {
      const base = (p.fcls || []).reduce((sum, f) => sum + (f.precio || 0), 0);
      return {
        nombre: p.id,
        base: base,
        adjudicado: p.precioAdjudicado,
      };
    });

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">Reportes y Analítica</h1>
          <p className="text-muted-foreground mt-1">Visualización de datos y métricas</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue="general" className="space-y-6 bg-background min-h-screen">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="ofertas">Ofertas</TabsTrigger>
            <TabsTrigger value="publicaciones">Ofertas</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6 bg-background min-h-screen">
            <div className="grid gap-6 sm:grid-cols-2">
              {/* KPIs */}
              <Card>
                <CardHeader>
                  <CardTitle>Totales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Ofertas</div>
                      <div className="text-2xl font-bold">{stats?.total_publicaciones || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Ofertas</div>
                      <div className="text-2xl font-bold">{stats?.total_ofertas || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Adjudicados</div>
                      <div className="text-2xl font-bold">{stats?.publicaciones_adjudicadas || stats?.vehiculos_adjudicados || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Tasa Adjudicación</div>
                      <div className="text-2xl font-bold">{stats?.tasa_adjudicacion || 0}%</div>
                    </div>
                    {isSuperAdmin && renta !== null && (
                      <div className="col-span-2 pt-2 border-t mt-1">
                        <div className="flex items-center justify-between">
                          <div className="text-muted-foreground font-medium">Renta Generada (0.5%)</div>
                          <div className="text-xl font-extrabold text-primary">
                            ${renta.renta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Estados */}
              <Card>
                <CardHeader>
                  <CardTitle>Estados de Publicaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="chart-status-bar">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadosData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value">
                          {estadosData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ofertas" className="space-y-6 bg-background min-h-screen">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas por Día</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-offers-by-day">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ofertasPorDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="offers" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {preciosComparacion.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Precio Base vs Adjudicado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={preciosComparacion}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="base" fill="hsl(var(--muted-foreground))" name="Precio Base" />
                        <Bar dataKey="adjudicado" fill="hsl(var(--primary))" name="Precio Adjudicado" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="publicaciones" className="space-y-6 bg-background min-h-screen">
            {distribucionData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Distribución por Frigorífico</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="chart-brand-treemap">
                    <ResponsiveContainer width="100%" height="100%">
                      <Treemap
                        data={distribucionData}
                        dataKey="value"
                        aspectRatio={4 / 3}
                        stroke="#fff"
                        content={(props) => {
                          const { x, y, width, height, index, name, value } = props;
                          return (
                            <g>
                              <rect
                                x={x}
                                y={y}
                                width={width}
                                height={height}
                                style={{
                                  fill: COLORS[index % COLORS.length],
                                  stroke: '#fff',
                                  strokeWidth: 2,
                                }}
                              />
                              {width > 30 && height > 30 && (
                                <text x={x + width / 2} y={y + height / 2} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold">
                                  {name}
                                </text>
                              )}
                              {width > 30 && height > 45 && (
                                <text x={x + width / 2} y={y + height / 2 + 15} textAnchor="middle" fill="#fff" fontSize={10}>
                                  {value}
                                </text>
                              )}
                            </g>
                          );
                        }}
                      >
                        <Tooltip />
                      </Treemap>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Resumen de Publicaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Promedio FCLs por Pub</div>
                      <div className="text-xl font-bold">
                        {publicaciones.length > 0
                          ? Math.round(publicaciones.reduce((sum, p) => sum + (p.fcls?.length || 0), 0) / publicaciones.length).toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Peso Promedio (kg)</div>
                      <div className="text-xl font-bold">
                        {publicaciones.length > 0
                          ? Math.round(publicaciones.reduce((sum, p) => sum + (p.fcls || []).reduce((s, f) => s + (f.peso || 0), 0), 0) / publicaciones.length).toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Precio Base Promedio</div>
                      <div className="text-xl font-bold">
                        ${publicaciones.length > 0
                          ? Math.round(publicaciones.reduce((sum, p) => sum + (p.fcls || []).reduce((s, f) => s + (f.precio || 0), 0), 0) / publicaciones.length).toLocaleString('en-US')
                          : 0}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Reportes;